"use client";

import React from "react";
import { translateGrade } from "@/common";

interface GradeSelectorProps {
    subjectName: string;
    grades: string[];
    value: string;
    language: string;
    onSelect: (grade: string) => void;
    /** Compact sizing for the narrow (mobile) table. */
    compact?: boolean;
    /**
     * The saved grades are not known yet. The buttons are laid out but not painted, so this
     * subject never shows a selected state that a later render has to take back.
     */
    pending?: boolean;
}

/**
 * A row of grade buttons — one per level the subject offers — replacing the
 * old -/+ stepper. Picking a level is a single click instead of repeated
 * taps, so the whole grade scale is visible at a glance. "N/A" is a stored
 * "not set" value, not a level, so it is never offered as a button.
 */
export const GradeSelector: React.FC<GradeSelectorProps> = ({
    subjectName,
    grades,
    value,
    language,
    onSelect,
    compact = false,
    pending = false,
}) => (
    <div
        // `invisible` (visibility: hidden) rather than unmounting: the buttons keep their exact
        // size, so the row does not reflow when the grades arrive, and they are non-interactive
        // and out of the accessibility tree while pending. This is what stops the core subjects
        // flickering on a cold load — see the comment on `inputsReady` in HomePage. The elective
        // rows have always behaved this way, because they do not exist until their grades do.
        className={`flex flex-wrap items-center gap-1 ${pending ? "invisible" : ""}`}
        role="group"
        aria-label={subjectName}
        aria-busy={pending || undefined}
        data-subject={subjectName}
    >
        {grades.filter((grade) => grade !== "N/A").map((grade) => {
            const selected = value === grade;
            return (
                <button
                    key={grade}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onSelect(grade)}
                    className={`flex-1 rounded-md border font-semibold transition-colors cursor-pointer ${compact ? "min-w-[2.25rem] px-1 py-1.5 text-xs" : "min-w-[2.75rem] px-2 py-2 text-sm"
                        } ${selected
                            // `duration-0` makes the selected highlight appear in the frame it is
                            // set, instead of fading in. Tailwind v4 registers the gradient stops as
                            // animatable colour properties (`@property --tw-gradient-from`, initial
                            // value transparent) and lists them in `transition-colors`, so the
                            // highlight was being interpolated from transparent over 150ms. That is
                            // invisible on an idle thread, but the page restores saved grades and
                            // then immediately scores every programme, and a blocked thread produces
                            // no frames — leaving the fade stranded near zero and the user's saved
                            // levels looking unselected for as long as the calculation ran. Hover
                            // (below) keeps its transition; only the selected state is instant.
                            ? "border-transparent bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-sm duration-0"
                            : "border-gray-300 bg-white text-gray-700 hover:border-rose-400 hover:bg-rose-50"
                        }`}
                >
                    {translateGrade(grade, language)}
                </button>
            );
        })}
    </div>
);
